import { getExamSectionKey } from "../../../../shared/utilities/helpers/examSectionKey";
import {
  applyQuestionAnswerEvaluation,
  getUserSelectedOptionIds,
  initExerciseQuestionClientState,
} from "../../../../containers/logged_user_components/evaluation_components/exercise_related_components/exercise_Test_component/exerciseTest_content_component/exercisePaperIdUtils";

const CKFINDER_BASE_URL = "http://quizmaster.motion.ac.in/ckfinder";

const ANSWER_TYPE_LABELS = {
  1: "Single-Correct-Type",
  2: "Multiple-Correct-Type",
  3: "Integer-Type",
};

// Exercise/DPP payloads ship relative ckfinder asset paths that only resolve
// against the quizmaster host.
export const resolveCkfinderPaths = (html) => {
  if (typeof html !== "string" || !html) return html;
  return html.replace(/(src\s*=\s*")\.?\/?ckfinder/gi, `$1${CKFINDER_BASE_URL}`);
};

export const getExerciseAnswerTypeId = (question) => {
  const rawType = String(
    question?.answer_type || question?.type || ""
  ).toLowerCase();

  if (rawType.includes("integer") || rawType.includes("numeric")) return 3;
  if (rawType.includes("multiple")) return 2;
  return 1;
};

export const getExerciseQuestionTypeLabel = (question) => {
  const rawType = String(question?.answer_type || question?.type || "").trim();
  return rawType || ANSWER_TYPE_LABELS[getExerciseAnswerTypeId(question)];
};

const parseOptionPayload = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string" || !raw.trim()) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

export const normalizeOptionItem = (option, index) => {
  if (typeof option === "string") {
    return {
      option_id: index + 1,
      option: resolveCkfinderPaths(option),
    };
  }

  return {
    ...option,
    option_id: option?.option_id ?? option?.id ?? index + 1,
    option: resolveCkfinderPaths(
      option?.option ??
        option?.option_text ??
        option?.option_eng ??
        option?.text ??
        ""
    ),
  };
};

export const parseQuestionOptionList = (question, language = "en") => {
  const englishOptions = parseOptionPayload(
    question?.option?.length ? question.option : question?.options
  );
  const englishFallback = parseOptionPayload(question?.eng_option);

  const baseOptions = (englishOptions.length ? englishOptions : englishFallback).map(
    normalizeOptionItem
  );

  if (language !== "hi") return baseOptions;

  const hindiOptions = parseOptionPayload(
    question?.option_hindi?.length ? question.option_hindi : question?.h_option
  );
  if (!hindiOptions.length) return baseOptions;

  return baseOptions.map((option, index) => ({
    ...option,
    option: resolveCkfinderPaths(
      hindiOptions[index]?.option ||
        hindiOptions[index]?.option_text ||
        hindiOptions[index] ||
        option.option
    ),
  }));
};

export const getExerciseSectionKey = (question) => {
  const sectionKey = getExamSectionKey(question);
  return sectionKey || "All Questions";
};

export const hasExerciseAnswer = (question) => {
  if (getExerciseAnswerTypeId(question) === 3) {
    return String(question?.integer_answer ?? "").trim() !== "";
  }
  return getUserSelectedOptionIds(question).length > 0;
};

/** Grid/overview "Answered" only after Save & Next (or an equivalent commit). */
export const hasCommittedExerciseAnswer = (question) =>
  Number(question?.is_saved) === 1;

export const resolveExerciseIsSaved = (question = {}) => {
  if (
    question.is_saved === undefined ||
    question.is_saved === null ||
    question.is_saved === ""
  ) {
    return hasExerciseAnswer(question) ? 1 : 0;
  }
  return Number(question.is_saved) === 1 ? 1 : 0;
};

export const withCommittedExerciseAnswer = (question) => {
  if (!question) return question;

  const evaluated = applyQuestionAnswerEvaluation({ ...question });
  return {
    ...evaluated,
    is_saved: hasExerciseAnswer(evaluated) ? 1 : resolveExerciseIsSaved(question),
  };
};

export const getExerciseQuestionStatus = (question, index, currentIndex) => {
  if (index === currentIndex) return "current";
  if (!question?.visited) return "unvisited";

  const answered = hasCommittedExerciseAnswer(question);
  if (question?.marked) return answered ? "answered_marked" : "marked";
  return answered ? "answered" : "unanswered";
};

export const getExerciseQuestionStats = (questions = [], currentIndex = 0) =>
  questions.reduce(
    (acc, question, index) => {
      const status = getExerciseQuestionStatus(question, index, currentIndex);

      if (status === "current") {
        acc.current = index + 1;
        const answered = hasCommittedExerciseAnswer(question);
        if (question?.marked) {
          if (answered) acc.answeredMarked += 1;
          else acc.marked += 1;
        } else if (answered) {
          acc.answered += 1;
        } else {
          acc.notAnswered += 1;
        }
        return acc;
      }

      if (status === "unvisited") acc.notVisited += 1;
      else if (status === "marked") acc.marked += 1;
      else if (status === "answered") acc.answered += 1;
      else if (status === "answered_marked") acc.answeredMarked += 1;
      else if (status === "unanswered") acc.notAnswered += 1;

      return acc;
    },
    {
      notVisited: 0,
      marked: 0,
      answered: 0,
      answeredMarked: 0,
      notAnswered: 0,
      current: 0,
    }
  );

export const getExerciseSections = (questions = []) => {
  const sections = [];
  questions.forEach((question) => {
    const sectionKey = getExerciseSectionKey(question);
    if (!sections.includes(sectionKey)) {
      sections.push(sectionKey);
    }
  });
  return sections;
};

export const groupExerciseQuestionsBySection = (questions = []) => {
  const grouped = [];
  const bySection = {};

  questions.forEach((question, index) => {
    const sectionKey = getExerciseSectionKey(question);
    if (!bySection[sectionKey]) {
      bySection[sectionKey] = [];
      grouped.push({ section: sectionKey, questions: bySection[sectionKey] });
    }
    bySection[sectionKey].push({ question, index });
  });

  return grouped;
};

export const getExerciseQuestionOptions = (question, language = "en") =>
  parseQuestionOptionList(question, language);

export const getExerciseQuestionText = (question, language = "en") => {
  if (language === "hi") {
    return question?.q_text_hindi || question?.q_text || "";
  }
  return question?.q_text || "";
};

export const isOptionSelected = (question, optionId) =>
  (question?.userAnswerCollection || []).some(
    (answer) => String(answer?.option_id) === String(optionId)
  );

const stripExerciseOptionUserAnswers = (options = []) =>
  (options || []).map((option) =>
    option && typeof option === "object"
      ? { ...option, is_user_correct_ans: 0 }
      : option
  );

export const normalizeExerciseExamQuestions = (questions = [], paperId) =>
  (questions || []).map((rawQuestion) => {
    const question = initExerciseQuestionClientState({
      ...rawQuestion,
      paper_id: rawQuestion?.paper_id ?? paperId,
    });

    question.q_text = resolveCkfinderPaths(question.q_text);
    question.q_text_hindi = resolveCkfinderPaths(question.q_text_hindi);
    question.solution = resolveCkfinderPaths(question.solution);
    question.sol_text = resolveCkfinderPaths(question.sol_text);
    question.option = stripExerciseOptionUserAnswers(
      parseQuestionOptionList(question, "en")
    );
    question.option_hindi = stripExerciseOptionUserAnswers(
      parseOptionPayload(question.option_hindi).map(normalizeOptionItem)
    );
    question.integer_answer = "";
    question.marked = false;
    question.visited = false;
    question.is_saved = 0;
    question.detailsFetched = question.option.length > 0;

    return question;
  });

export const mergePyqQuestionDetails = (
  listQuestion = {},
  details,
  { applyBackendUserAnswers = false } = {}
) => {
  if (!details) {
    return { ...listQuestion, detailsFetched: true };
  }

  const options = parseQuestionOptionList(details, "en");
  const hindiOptions = parseOptionPayload(
    details.option_hindi?.length ? details.option_hindi : details.h_option
  ).map(normalizeOptionItem);

  let userAnswerCollection = listQuestion.userAnswerCollection || [];
  if (applyBackendUserAnswers && !userAnswerCollection.length) {
    userAnswerCollection = options
      .filter((option) => Number(option.is_user_correct_ans) === 1)
      .map((option) => ({ option_id: option.option_id }));
  }

  return applyQuestionAnswerEvaluation({
    ...listQuestion,
    ...details,
    paper_id: listQuestion.paper_id ?? details.paper_id,
    q_text: resolveCkfinderPaths(details.q_text || listQuestion.q_text),
    q_text_hindi: resolveCkfinderPaths(
      details.q_text_hindi || listQuestion.q_text_hindi
    ),
    option: options.length
      ? stripExerciseOptionUserAnswers(options)
      : stripExerciseOptionUserAnswers(listQuestion.option || []),
    option_hindi: hindiOptions.length
      ? stripExerciseOptionUserAnswers(hindiOptions)
      : stripExerciseOptionUserAnswers(listQuestion.option_hindi || []),
    userAnswerCollection,
    integer_answer: applyBackendUserAnswers
      ? listQuestion.integer_answer || details.integer_answer || ""
      : listQuestion.integer_answer || "",
    marked: listQuestion.marked === true,
    visited: true,
    detailsFetched: true,
    is_saved:
      Number(listQuestion.is_saved) === 1 ||
      (applyBackendUserAnswers &&
        !(listQuestion.userAnswerCollection || []).length &&
        !String(listQuestion.integer_answer || "").trim() &&
        (userAnswerCollection.length > 0 ||
          String(details.integer_answer || "").trim() !== ""))
        ? 1
        : 0,
    totalTimeTaken: listQuestion.totalTimeTaken || 0,
    isReview: listQuestion.isReview,
    isAttempted: listQuestion.isAttempted,
    isAnswerGuessed: listQuestion.isAnswerGuessed,
  });
};

// Homework single-question responses store a resumed MCQ choice in
// integer_answer, matching the previous homework test screen.
export const mergeHomeworkQuestionDetails = (listQuestion = {}, details) => {
  const merged = mergePyqQuestionDetails(listQuestion, details, {
    applyBackendUserAnswers: true,
  });

  if (!details || getExerciseAnswerTypeId(merged) === 3) {
    return merged;
  }

  if ((merged.userAnswerCollection || []).length) {
    return merged;
  }

  const savedOptionId = details.integer_answer;
  if (
    savedOptionId === undefined ||
    savedOptionId === null ||
    String(savedOptionId).trim() === ""
  ) {
    return merged;
  }

  return applyQuestionAnswerEvaluation({
    ...merged,
    userAnswerCollection: [{ option_id: savedOptionId }],
    is_saved: 1,
  });
};

export const PYQ_PRACTICE_SUMMARY_KEY = "pyqPracticeSummary";
export const EXERCISE_LIVE_PROGRESS_KEY = "exerciseExamLiveProgress";
export const HOMEWORK_RESUME_MAP_KEY = "homeworkResumeProgressMap";

export const getHomeworkPaperId = (testDetails = {}) =>
  String(testDetails?.id ?? testDetails?.paper_id ?? "");

const readHomeworkResumeMap = () => {
  try {
    const stored = localStorage.getItem(HOMEWORK_RESUME_MAP_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const toHomeworkResumeAnswers = (questions = []) =>
  (questions || []).map((question) => ({
    qid: question.qid,
    userAnswerCollection: question.userAnswerCollection || [],
    integer_answer: question.integer_answer ?? "",
    isAttempted: Boolean(question.isAttempted),
    isAnswerCorrect: question.isAnswerCorrect ?? 0,
    totalTimeTaken: question.totalTimeTaken ?? 0,
    isReview: Boolean(question.isReview || question.marked),
    marked: Boolean(question.marked),
    visited: Boolean(question.visited),
    is_saved: Number(question.is_saved) === 1 ? 1 : 0,
    isAnswerGuessed: Boolean(question.isAnswerGuessed),
    answerChecked: question.answerChecked === true,
    showSolution: question.showSolution === true,
  }));

export const persistHomeworkResumeProgress = ({
  paperId,
  currentQuestion = 0,
  timerSeconds = 0,
  questions = [],
}) => {
  const id = String(paperId || "");
  if (!id) return;

  try {
    const map = readHomeworkResumeMap();
    map[id] = {
      currentQuestion,
      timerSeconds: Number(timerSeconds) || 0,
      updatedAt: Date.now(),
      answers: toHomeworkResumeAnswers(questions),
    };
    localStorage.setItem(HOMEWORK_RESUME_MAP_KEY, JSON.stringify(map));
  } catch (error) {
    console.log(error);
  }
};

export const readHomeworkResumeProgress = (paperId) => {
  const id = String(paperId || "");
  if (!id) return null;
  const entry = readHomeworkResumeMap()[id];
  return entry || null;
};

export const clearHomeworkResumeProgress = (paperId) => {
  const id = String(paperId || "");
  if (!id) return;

  try {
    const map = readHomeworkResumeMap();
    if (!map[id]) return;
    delete map[id];
    localStorage.setItem(HOMEWORK_RESUME_MAP_KEY, JSON.stringify(map));
  } catch (error) {
    console.log(error);
  }
};

export const isHomeworkInProgress = (item = {}) => {
  if (String(item?.status || "").toLowerCase() !== "pending") return false;
  if (readHomeworkResumeProgress(getHomeworkPaperId(item))) return true;

  const lastQuestionId = item?.userGivenLastQuestionId ?? item?.last_question_id;
  if (lastQuestionId && Number(lastQuestionId) !== 0) return true;
  if (
    item?.is_resume === true ||
    item?.is_resume === 1 ||
    item?.is_resume === "1"
  ) {
    return true;
  }

  return false;
};

export const buildExerciseLiveProgressIdentity = ({
  testType,
  testDetails = {},
  topicData = {},
}) => ({
  testType: testType || "",
  exerciseType:
    testType === "homework"
      ? String(testDetails?.id ?? testDetails?.paper_id ?? "")
      : testDetails?.excersie_type || "",
  topicId: topicData?.topicId ?? "",
  videoId: testDetails?.videoId ?? "",
  paperId: testDetails?.paper_id ?? "",
});

const exerciseLiveIdentitiesMatch = (left, right) =>
  String(left?.testType || "") === String(right?.testType || "") &&
  String(left?.exerciseType || "") === String(right?.exerciseType || "") &&
  String(left?.topicId ?? "") === String(right?.topicId ?? "") &&
  String(left?.videoId ?? "") === String(right?.videoId ?? "");

export const persistExerciseLiveProgress = ({
  identity,
  questions = [],
  currentQuestion = 0,
  timerSeconds = 0,
}) => {
  if (!identity?.testType) return;

  try {
    sessionStorage.setItem(
      EXERCISE_LIVE_PROGRESS_KEY,
      JSON.stringify({
        identity,
        currentQuestion,
        timerSeconds: Number(timerSeconds) || 0,
        updatedAt: Date.now(),
        answers: questions.map((question) => ({
          qid: question.qid,
          userAnswerCollection: question.userAnswerCollection || [],
          integer_answer: question.integer_answer ?? "",
          isAttempted: Boolean(question.isAttempted),
          isAnswerCorrect: question.isAnswerCorrect ?? 0,
          totalTimeTaken: question.totalTimeTaken ?? 0,
          isReview: Boolean(question.isReview || question.marked),
          marked: Boolean(question.marked),
          visited: Boolean(question.visited),
          is_saved: Number(question.is_saved) === 1 ? 1 : 0,
          isAnswerGuessed: Boolean(question.isAnswerGuessed),
          answerChecked: question.answerChecked === true,
          showSolution: question.showSolution === true,
        })),
      })
    );
  } catch (error) {
    console.log(error);
  }
};

export const readExerciseLiveProgress = (identity) => {
  try {
    const stored = sessionStorage.getItem(EXERCISE_LIVE_PROGRESS_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);
    if (!exerciseLiveIdentitiesMatch(parsed?.identity, identity)) {
      return null;
    }

    return parsed;
  } catch (error) {
    return null;
  }
};

export const clearExerciseLiveProgress = (identity) => {
  try {
    if (!identity) {
      sessionStorage.removeItem(EXERCISE_LIVE_PROGRESS_KEY);
      return;
    }

    const stored = sessionStorage.getItem(EXERCISE_LIVE_PROGRESS_KEY);
    if (!stored) return;

    const parsed = JSON.parse(stored);
    if (exerciseLiveIdentitiesMatch(parsed?.identity, identity)) {
      sessionStorage.removeItem(EXERCISE_LIVE_PROGRESS_KEY);
    }
  } catch (error) {
    console.log(error);
  }
};

const BREAKDOWN_STYLES = {
  attempted: {
    bg_color: "#E8F8EF",
    border_color: "#17B26A",
    value_text_color: "#067647",
  },
  unattempted: {
    bg_color: "#FEE4E2",
    border_color: "#F04438",
    value_text_color: "#B42318",
  },
  marked: {
    bg_color: "#F4EBFF",
    border_color: "#9B8AFB",
    value_text_color: "#5925DC",
  },
  notVisited: {
    bg_color: "#F2F4F7",
    border_color: "#D0D5DD",
    value_text_color: "#475467",
  },
};

export const buildPyqPracticeSummary = ({
  questions = [],
  testDetails = {},
  timeSpentSeconds = 0,
  paperId,
}) => {
  const total = questions.length;
  const attemptedCount = questions.filter((question) =>
    hasCommittedExerciseAnswer(question)
  ).length;
  const correctCount = questions.filter(
    (question) =>
      hasCommittedExerciseAnswer(question) &&
      Number(question.isAnswerCorrect) === 1
  ).length;
  const markedCount = questions.filter((question) => question?.marked).length;
  const notVisitedCount = questions.filter(
    (question) => !question?.visited
  ).length;
  const accuracy =
    attemptedCount > 0
      ? Number(((correctCount / attemptedCount) * 100).toFixed(2))
      : 0;

  const testName = [testDetails.topic_name, testDetails.format]
    .filter(Boolean)
    .join(" • ");

  return {
    test_name: testName || "PYQ Practice",
    attempted: `${attemptedCount}/${total}`,
    time_spent: formatExerciseTimer(timeSpentSeconds),
    accuracy,
    question_breakdown: [
      {
        id: "attempted",
        label: "Attempted",
        value: attemptedCount,
        ...BREAKDOWN_STYLES.attempted,
      },
      {
        id: "unattempted",
        label: "Unattempted",
        value: Math.max(total - attemptedCount, 0),
        ...BREAKDOWN_STYLES.unattempted,
      },
      {
        id: "marked",
        label: "Mark for Review",
        value: markedCount,
        ...BREAKDOWN_STYLES.marked,
      },
      {
        id: "notVisited",
        label: "Not Visited",
        value: notVisitedCount,
        ...BREAKDOWN_STYLES.notVisited,
      },
    ],
    paper_id: paperId ?? testDetails.paper_id ?? null,
    returnTo: testDetails.returnTo || "/topic_component",
    returnState: testDetails.returnState || { tab: "pyq" },
  };
};

export const HOMEWORK_PRACTICE_SUMMARY_KEY = "homeworkPracticeSummary";

export const persistHomeworkPracticeSummary = (summary) => {
  try {
    sessionStorage.setItem(
      HOMEWORK_PRACTICE_SUMMARY_KEY,
      JSON.stringify(summary)
    );
  } catch (error) {
    console.log(error);
  }
};

export const readHomeworkPracticeSummary = () => {
  try {
    const stored = sessionStorage.getItem(HOMEWORK_PRACTICE_SUMMARY_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

export const persistPyqPracticeSummary = (summary) => {
  try {
    sessionStorage.setItem(PYQ_PRACTICE_SUMMARY_KEY, JSON.stringify(summary));
  } catch (error) {
    console.log(error);
  }
};

export const readPyqPracticeSummary = () => {
  try {
    const stored = sessionStorage.getItem(PYQ_PRACTICE_SUMMARY_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    return null;
  }
};

export const formatExerciseTimer = (totalSeconds) => {
  const safeSeconds = Math.max(Number(totalSeconds) || 0, 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return [hours, minutes, seconds]
    .map((unit) => String(unit).padStart(2, "0"))
    .join(":");
};
